import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function SubscriptionsPanel({ plans, reload }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", plan_id: "", status: "active", notes: "" });
  const STATUSES = ["active", "paused", "canceled", "pending_activation", "incomplete"];

  const load = async () => {
    setLoading(true);
    try { const { data } = await http.get("/admin/subscriptions"); setSubs(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.plan_id) { toast.error("Email and plan are required."); return; }
    setCreating(true);
    try {
      await http.post("/admin/subscriptions", form);
      toast.success("Subscription created.");
      setForm({ email: "", plan_id: "", status: "active", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setCreating(false); }
  };

  const update = async (sid, patch) => {
    try {
      await http.put(`/admin/subscriptions/${sid}`, patch);
      toast.success("Updated.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  };

  const remove = async (sid) => {
    if (!confirm("Delete this subscription record permanently?")) return;
    await http.delete(`/admin/subscriptions/${sid}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div className="space-y-6" data-testid="subs-panel">
      <div className="border border-[#C9A055]/40 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">Manual Activation</div>
        <p className="font-body text-[12px] text-[#8a857d] mb-4">Subscriptions are usable today — Stripe doesn't have to be live. Create active subscribers manually (e.g., to comp a friend or onboard your first 10 members) and they'll show up in their account immediately.</p>
        <div className="grid md:grid-cols-5 gap-2">
          <input className="stc-input md:col-span-2" placeholder="customer@email.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="sub-create-email"/>
          <select className="stc-input" value={form.plan_id} onChange={e => setForm({...form, plan_id: e.target.value})} data-testid="sub-create-plan">
            <option value="">Pick a plan…</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name} — ${p.price}/{p.frequency}</option>)}
          </select>
          <select className="stc-input" value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="sub-create-status">
            {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
          <button disabled={creating} onClick={create} className="btn-gold" data-testid="sub-create-btn"><Plus size={14}/> Add</button>
        </div>
        <input className="stc-input mt-2" placeholder="Internal note (optional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="sub-create-notes"/>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a]" data-testid="subs-list">
        {loading ? <div className="p-6 text-[#8a857d]">Loading…</div> :
          subs.length === 0 ? <div className="p-6 text-[#8a857d]">No subscriptions yet. Create one above.</div> :
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Email</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Plan</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Status</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Started</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Source</th>
                <th className="px-3 py-2.5 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`sub-row-${s.id}`}>
                  <td className="px-3 py-2 text-[#F4F1EA]">{s.email || s.user?.email || "—"}</td>
                  <td className="px-3 py-2">
                    <select value={s.plan_id || ""} onChange={e => update(s.id, { plan_id: e.target.value })} className="stc-input py-1 text-xs" data-testid={`sub-plan-${s.id}`}>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name} (${p.price}/{p.frequency})</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select value={s.status} onChange={e => update(s.id, { status: e.target.value })} className={`stc-input py-1 text-xs ${s.status === "active" ? "text-emerald-400" : s.status === "canceled" ? "text-red-400" : "text-amber-400"}`} data-testid={`sub-status-${s.id}`}>
                      {STATUSES.map(st => <option key={st} value={st}>{st.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-[#B5B0A7] text-xs">{s.started_at ? new Date(s.started_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-[#8a857d] text-xs uppercase tracking-[0.18em] font-ui">{s.source || (s.stripe_session_id ? "stripe" : "—")}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(s.id)} className="text-red-400 hover:text-red-300" title="Delete record" data-testid={`sub-delete-${s.id}`}><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}
